import { paymentTierAmount } from "@/types/payments";
import type { Dictionary } from "@/types/i18n";

const idrFormatter = new Intl.NumberFormat("id-ID");

export function formatPrice(value: number) {
  return `Rp.${idrFormatter.format(value)}`;
}

/** Pulls the integer rupiah amount out of a display string like "Rp.350.000". */
export function parsePriceDigits(label: string) {
  const digits = label.replace(/\D/g, "");
  return digits ? Number(digits) : 0;
}

export type OfferCtaKind = "free" | "upload" | "module" | "deprecated";
export type OfferCategory = "free" | "career-os" | "cv-review";
export type OfferIconKey =
  | "file-check"
  | "scan-line"
  | "layout-template"
  | "file-text"
  | "message-square"
  | "target"
  | "linkedin";

export interface OfferItem {
  id: string;
  name: string;
  priceLabel: string;
  priceSuffix?: string;
  description: string;
  features: string[];
  cta: OfferCtaKind;
  ctaLabel?: string;
  href?: string;
  note?: string;
  category: OfferCategory;
  icon: OfferIconKey;
  isFree: boolean;
  featured: boolean;
}

export interface OfferCatalog {
  free: OfferItem[];
  modules: OfferItem[];
  cvReview: OfferItem[];
  /** Everything, ordered free -> modules -> cv-review (deprecated last). */
  all: OfferItem[];
}

/**
 * Builds the offer catalog from i18n copy and the canonical price table in
 * {@link paymentTierAmount}. Free tools link to their public landing pages,
 * CV-review tiers route into the review flow (they need an uploaded CV), and
 * Career OS modules link into the Career OS dashboard.
 */
export function buildOfferCatalog({
  locale,
  dictionary,
  gateToSignUp = false,
}: {
  locale: string;
  dictionary: Dictionary;
  /**
   * Public marketing surfaces can't reach the auth-gated Career OS modules, so
   * route those CTAs through sign-up (preserving the offer page as the
   * post-login destination) instead of straight into the dashboard.
   */
  gateToSignUp?: boolean;
}): OfferCatalog {
  const copy = dictionary.dashboard.offerPage.products;
  const reviewHref = `/${locale}/review-cv-gratis`;
  const careerOsHref = gateToSignUp
    ? `/${locale}/sign-up?redirect=${encodeURIComponent(`/${locale}/dashboard/offer`)}`
    : `/${locale}/dashboard/career-os`;
  const perReview = copy.perReview;

  const free: OfferItem[] = [
    {
      id: "free-review",
      name: copy.freeReview.name,
      priceLabel: copy.freeReview.price,
      description: copy.freeReview.description,
      features: [...copy.freeReview.features],
      cta: "free",
      ctaLabel: copy.freeReview.cta,
      href: reviewHref,
      category: "free",
      icon: "file-check",
      isFree: true,
      featured: true,
    },
    {
      id: "ats-check",
      name: copy.atsCheck.name,
      priceLabel: copy.atsCheck.price,
      description: copy.atsCheck.description,
      features: [...copy.atsCheck.features],
      cta: "free",
      ctaLabel: copy.atsCheck.cta,
      href: `/${locale}/cek-cv-ats`,
      category: "free",
      icon: "scan-line",
      isFree: true,
      featured: true,
    },
    {
      id: "ats-templates",
      name: copy.templates.name,
      priceLabel: copy.templates.price,
      description: copy.templates.description,
      features: [...copy.templates.features],
      cta: "free",
      ctaLabel: copy.templates.cta,
      href: `/${locale}/template-cv-ats`,
      category: "free",
      icon: "layout-template",
      isFree: true,
      featured: true,
    },
  ];

  // The two live CV tiers reuse the canonical pricing copy from payment.tiers.
  const liveTiers: OfferItem[] = dictionary.payment.tiers.map((tier) => ({
    id: tier.id,
    name: tier.name,
    priceLabel: formatPrice(tier.price),
    priceSuffix: perReview,
    description: tier.description,
    features: [...tier.features],
    cta: "upload" as const,
    ctaLabel: copy.ctaUpload,
    href: reviewHref,
    category: "cv-review" as const,
    icon: "file-text" as const,
    isFree: false,
    featured: false,
  }));

  const deprecatedReview: OfferItem = {
    id: "unlock-review",
    name: copy.deprecatedReview.name,
    priceLabel: formatPrice(paymentTierAmount["Unlock CV Review"]),
    priceSuffix: perReview,
    description: copy.deprecatedReview.description,
    features: [...copy.deprecatedReview.features],
    cta: "deprecated",
    note: copy.deprecatedNote,
    category: "cv-review",
    icon: "file-text",
    isFree: false,
    featured: false,
  };

  const moduleDefs = [
    {
      id: "interview",
      tier: "Interview Session" as const,
      icon: "message-square" as const,
      featured: true,
      ...copy.modules.interview,
    },
    {
      id: "cv-personalization",
      tier: "Career OS - CV Personalization" as const,
      icon: "file-text" as const,
      featured: true,
      ...copy.modules.cvPersonalization,
    },
    {
      id: "linkedin",
      tier: "Career OS - LinkedIn Optimization" as const,
      icon: "linkedin" as const,
      featured: true,
      ...copy.modules.linkedin,
    },
    {
      id: "ideal-job-fit",
      tier: "Career OS - Ideal Job Fit" as const,
      icon: "target" as const,
      featured: false,
      ...copy.modules.idealJobFit,
    },
  ];

  const modules: OfferItem[] = moduleDefs.map((entry) => ({
    id: entry.id,
    name: entry.name,
    priceLabel: formatPrice(paymentTierAmount[entry.tier]),
    description: entry.description,
    features: [...entry.features],
    cta: "module" as const,
    ctaLabel: copy.ctaModule,
    href: careerOsHref,
    category: "career-os" as const,
    icon: entry.icon,
    isFree: false,
    featured: entry.featured,
  }));

  const cvReview = [...liveTiers, deprecatedReview];

  return {
    free,
    modules,
    cvReview,
    all: [...free, ...modules, ...cvReview],
  };
}
